import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { fetchVenueList } from '~/api/wallet'
import { useHomeAggregate } from '~/pages/index/composables/useHomeAggregate'
import type { VenueListDto, VenueTransferResponseDto } from '~/types/api/venues'
import type { TransferVenue, VenueCat } from '~/utils/transfer/types'
import { formatBaseAmt, parseMoney } from '~/utils/transfer/money'
import { mapVenueList } from '~/utils/transfer/mapVenue'
import { VENUE_QUERY_KEY } from '~/utils/transfer/queryKeys'

const CAT_I18N: Record<string, string> = {
  slots: 'me.betTypeSlots',
  chess: 'me.betTypeChess',
  card: 'me.betTypeChess',
  cards: 'me.betTypeChess',
  fish: 'me.betTypeFish',
  fishing: 'me.betTypeFish',
  live: 'me.betTypeLive',
  original: 'me.betTypeOriginal',
  sport: 'me.betTypeSport',
  sports: 'me.betTypeSport',
}

const FALLBACK_CATS: VenueCat[] = ['slots', 'card', 'fish', 'live', 'sport']

/** 场馆目录 + 余额（GET /wallet/venues，额度转换 / 游戏浮层共用） */
export function useVenueCatalog() {
  const { t } = useI18n()
  const userStore = useUserStore()
  const queryClient = useQueryClient()
  const { open: transferOpen } = useTransferNav()
  const { open: gamePlayOpen } = useGamePlayNav()

  const catFilter = useState<VenueCat>('transfer-cat', () => '')
  const panelOpen = computed(() => transferOpen.value || gamePlayOpen.value)
  const listEnabled = computed(() => userStore.isLoggedIn && panelOpen.value)

  const listQuery = useQuery({
    queryKey: computed(() => [...VENUE_QUERY_KEY, catFilter.value]),
    queryFn: () => fetchVenueList(catFilter.value || undefined),
    enabled: listEnabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  })

  const homeQuery = useHomeAggregate({ enabled: panelOpen })

  const homeCats = computed(() =>
    (homeQuery.data.value?.categories ?? []).filter(c => c.id && c.id !== 'hot'),
  )

  const vendorCatMap = computed(() => {
    const map: Record<string, string> = {}
    for (const cat of homeCats.value) {
      for (const v of cat.vendors ?? []) {
        if (v.id && !map[v.id]) map[v.id] = cat.id
      }
    }
    return map
  })

  const listDto = computed(() => listQuery.data.value)
  const venues = computed(() =>
    mapVenueList(
      listDto.value,
      item => catFilter.value || vendorCatMap.value[item.vendor_code] || '',
    ),
  )
  const venueOf = (k: string) => venues.value.find(v => v.k === k)
  const venueBal = (v: TransferVenue) => (v.balKnown ? v.bal : 0)
  const formatCentral = (n: number) => formatBaseAmt(n, userStore.wallet.ledgerCurrency)
  /** balance_known=false 时展示「--」，避免把查不到误显示成 0 */
  const formatVenueAmt = (v: TransferVenue) =>
    v.balKnown ? formatCentral(v.bal) : t('me.tfUnknown')

  const catLabel = (cat: VenueCat) => {
    if (!cat) return ''
    const fromHome = homeCats.value.find(c => c.id === cat)?.label
    if (fromHome) return fromHome
    const key = CAT_I18N[cat]
    return key ? t(key) : cat
  }

  const cats = computed(() => {
    const fromHome = homeCats.value.map(c => ({
      key: c.id as VenueCat,
      label: c.label,
    }))
    const items = fromHome.length
      ? fromHome
      : FALLBACK_CATS.map(key => ({ key, label: catLabel(key) }))
    return [{ key: '' as VenueCat, label: t('me.tfAllCats') }, ...items]
  })

  const minTransfer = computed(() => {
    const display = listDto.value?.min_amount_display
    if (display) return parseMoney(display)
    return userStore.wallet.ledgerCurrency === 'CNY' ? 10 : 1
  })

  const patchVenueList = (res: VenueTransferResponseDto) => {
    queryClient.setQueriesData<VenueListDto>({ queryKey: [...VENUE_QUERY_KEY] }, old => {
      if (!old) return old
      return {
        ...old,
        central_available: res.central_available,
        central_available_display: res.central_available_display,
        venues: (old.venues ?? []).map(v =>
          v.vendor_code === res.vendor_code
            ? {
                ...v,
                balance: res.venue_balance,
                balance_display: res.venue_balance_display,
                balance_known: res.venue_balance_known,
              }
            : v,
        ),
      }
    })
  }

  return {
    venues,
    listDto,
    listFetching: computed(() => listQuery.isFetching.value),
    listPending: computed(() => listQuery.isLoading.value),
    catFilter,
    cats,
    venueOf,
    venueBal,
    formatCentral,
    formatVenueAmt,
    catLabel,
    minTransfer,
    patchVenueList,
    refreshVenues: () => queryClient.invalidateQueries({ queryKey: [...VENUE_QUERY_KEY] }),
  }
}
